import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import './SelectPatientStyle.css'
import {
  Badge,
  DataTable,
  EmptyState,
  Modal,
  Search,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { fetchAllPatients } from '@store/actions/patientmanagement.action'
import { Patient } from '@dataTypes/PatientInfo.types'
import { getFullName } from '@utils/fullname.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { PATIENT_STATUS_BADGE_COLOR } from '@constants/PatientManagement/status'
import { IContact } from '@dataTypes/contact.type'

type SelectPatientModalProps = {
  isOpen: boolean
  onSelectPatient: (patient: Patient, contact?: IContact) => void
  onHide?: () => void
}

type PatientRow = {
  id: string
  name: string
  status: string
}

const SELECT_PATIENT_LOADING_CONTAINER = 'body'
const DEFAULT_PAGE_SIZE = 10

const runAfterLoadingPaint = (callback: () => void): void => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(callback)
  })
}

const compareText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })

const SelectPatientModal: React.FC<SelectPatientModalProps> = ({
  isOpen,
  onSelectPatient,
  onHide,
}) => {
  const dispatch = useAppDispatch()
  const patientsState = useAppSelector((state) => state.patient.data)
  const hasLoadedPatients = useAppSelector(
    (state) => state.patient.hasLoadedPatients,
  )
  const isPatientListLoading = useAppSelector((state) => state.patient.loading)
  const clinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const contacts = useAppSelector((state) => state.contacts.data)
  const [searchPatient, setSearchPatient] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [forceClose, setForceClose] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<'name' | 'status'>('name')
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const isConfirmingRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const isListLoading = !clinicId || isPatientListLoading || !hasLoadedPatients
  const showModal = isOpen && !forceClose

  useEffect(() => {
    if (!isOpen) {
      setIsConfirming(false)
      // Keep overlay if we just confirmed so parent bootstrap loading can take over
      if (!isConfirmingRef.current) {
        hideLoading(SELECT_PATIENT_LOADING_CONTAINER)
      }
      return
    }
    isConfirmingRef.current = false
    setIsConfirming(false)
    setForceClose(false)
    if (!clinicId || hasLoadedPatients || isPatientListLoading) {
      return
    }
    void dispatch(fetchAllPatients())
  }, [
    isOpen,
    clinicId,
    dispatch,
    hasLoadedPatients,
    isPatientListLoading,
    hideLoading,
  ])

  useEffect(() => {
    if (!showModal || isListLoading) return
    const frame = window.requestAnimationFrame(() =>
      searchInputRef.current?.focus(),
    )
    return () => window.cancelAnimationFrame(frame)
  }, [showModal, isListLoading])

  const setDefaultValues = (): void => {
    setSearchPatient('')
    setSelectedPatient(null)
    setCurrentPage(1)
    setPageSize(DEFAULT_PAGE_SIZE)
    setSortColumnId('name')
    setSortDirection('asc')
  }

  const filteredPatients = useMemo((): Patient[] => {
    if (!Array.isArray(patientsState)) return []
    const query = searchPatient.trim().toLowerCase()
    const matches = patientsState.filter((patient) =>
      getFullName(patient).toLowerCase().includes(query),
    )
    // Status is the same for every row today, so name is the stable tiebreaker.
    const direction = sortDirection === 'asc' ? 1 : -1
    return matches.sort((a, b) => {
      const nameOrder = compareText(getFullName(a), getFullName(b))
      return sortColumnId === 'name' ? direction * nameOrder : nameOrder
    })
  }, [patientsState, searchPatient, sortColumnId, sortDirection])

  const hasNoPatients =
    !Array.isArray(patientsState) || patientsState.length === 0
  const showEmptyState = !isListLoading && filteredPatients.length === 0

  const totalPages = Math.max(1, Math.ceil(filteredPatients.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)

  const pagePatients = useMemo(
    () =>
      filteredPatients.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredPatients, safePage, pageSize],
  )

  useEffect(() => {
    if (showEmptyState) {
      setSelectedPatient(null)
    }
  }, [showEmptyState])

  const confirmSelection = (patient: Patient): void => {
    if (isConfirmingRef.current) return
    isConfirmingRef.current = true
    setIsConfirming(true)
    showLoading({ container: SELECT_PATIENT_LOADING_CONTAINER })
    const contact = contacts?.find((c) => c.patient_id === patient.patient_id)

    // Let the overlay render before patient bootstrap work begins.
    runAfterLoadingPaint(() => {
      onSelectPatient(patient, contact)
      setDefaultValues()
      // Parent loading replaces this container during slower bootstrap flows.
      window.setTimeout(() => {
        hideLoading(SELECT_PATIENT_LOADING_CONTAINER)
      }, 100)
    })
  }

  const handleSave = (): void => {
    if (selectedPatient && !isConfirmingRef.current) {
      confirmSelection(selectedPatient)
    }
  }

  const handleClose = (): void => {
    if (isConfirmingRef.current) return
    setForceClose(true)
    setDefaultValues()
    onHide?.()
  }

  const handleSort = (columnId: string): void => {
    if (columnId !== 'name' && columnId !== 'status') return
    if (columnId === sortColumnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumnId(columnId)
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const getPatientFromRowEvent = (
    event: React.MouseEvent<HTMLDivElement>,
  ): Patient | undefined => {
    const row = (event.target as HTMLElement).closest(
      'tbody > tr.ds-data-table__row',
    )
    if (!row?.parentElement) return undefined
    const index = Array.from(row.parentElement.children).indexOf(row)
    return pagePatients[index]
  }

  const handleTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (isConfirmingRef.current) return
    const patient = getPatientFromRowEvent(event)
    if (patient) setSelectedPatient(patient)
  }

  const handleTableDoubleClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    const patient = getPatientFromRowEvent(event)
    if (patient) confirmSelection(patient)
  }

  const columns: DataTableColumn<PatientRow>[] = [
    {
      id: 'name',
      label: 'Patient name',
      sortable: true,
    },
    {
      id: 'status',
      label: 'Patient status',
      sortable: true,
      width: 140,
      renderCell: (row) => (
        <Badge
          label={row.status}
          mode="tinted"
          color={
            PATIENT_STATUS_BADGE_COLOR[row.status as StatusTypes] ?? 'gray'
          }
        />
      ),
    },
  ]

  const rows: PatientRow[] = pagePatients.map((patient) => ({
    id: String(patient.patient_id),
    name: getFullName(patient),
    // The patient list only returns active patients for selection.
    status: StatusTypes.Active,
  }))

  return (
    <Modal
      open={showModal}
      onClose={handleClose}
      size="md"
      title="Select patient"
      aria-label="Select patient"
      closeOnBackdrop={false}
      secondaryLabel="Cancel"
      primaryLabel={isConfirming ? 'Selecting…' : 'Select'}
      primaryDisabled={!selectedPatient || isConfirming || isListLoading}
      onSecondaryClick={handleClose}
      onPrimaryClick={handleSave}
      className="select-patient-modal"
      bodyClassName="select-patient-modal__body"
    >
      <Search
        ref={searchInputRef}
        className="select-patient-modal__search"
        placeholder="Search patient name"
        ariaLabel="Search patient name"
        value={searchPatient}
        disabled={isListLoading}
        onChange={(value) => {
          setSearchPatient(value)
          setCurrentPage(1)
        }}
        onClear={() => {
          setSearchPatient('')
          setCurrentPage(1)
        }}
      />
      {showEmptyState ? (
        <EmptyState
          className="select-patient-modal__empty"
          iconClass="mgc mgc_user_search_fill"
          title="No patients found"
          description={
            hasNoPatients
              ? "You don't have any patients on record yet."
              : 'No patients match your search.'
          }
        />
      ) : (
        <div
          className="select-patient-modal__table"
          onClick={handleTableClick}
          onDoubleClick={handleTableDoubleClick}
        >
          <DataTable<PatientRow>
            columns={columns}
            rows={rows}
            state={isListLoading ? 'loading' : 'default'}
            showActions={false}
            selectedRowIds={
              selectedPatient ? [String(selectedPatient.patient_id)] : []
            }
            sortColumnId={sortColumnId}
            sortDirection={sortDirection}
            onSort={handleSort}
            pagination={{
              currentPage: safePage,
              totalPages,
              pageSize,
              onPageChange: setCurrentPage,
              onPageSizeChange: (size) => {
                setPageSize(size)
                setCurrentPage(1)
              },
            }}
          />
        </div>
      )}
    </Modal>
  )
}

export default SelectPatientModal
